import type { PlanResult } from "@decopl/deco-engine";
import {
  consumptionForSegments,
  depthToAmbient,
  overallCeilingDepth,
} from "@decopl/deco-engine";
import { useEffect, useMemo, useRef, useState } from "react";
import { gasName } from "../i18n.js";
import { useStore } from "../store.js";
import { Pause, Play, Reset } from "./Icons.js";

const BUBBLES = (() => {
  const arr: {
    seed: number;
    xOff: number;
    size: number;
    speed: number;
    period: number;
    wobble: number;
  }[] = [];
  for (let i = 0; i < 14; i++) {
    arr.push({
      seed: Math.random() * 1000,
      xOff: (Math.random() - 0.5) * 22,
      size: 1.5 + Math.random() * 2.5,
      speed: 8 + Math.random() * 12,
      period: 1.2 + Math.random() * 1.6,
      wobble: 1 + Math.random() * 3,
    });
  }
  return arr;
})();

interface Props {
  result: PlanResult | null;
}

export function DiverPreview({ result }: Props) {
  const { gases, gfHigh, sac, tankVolume, tankStartPressure, lang, audio } = useStore();
  const [speed, setSpeed] = useState(6);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);

  const audioCtxRef = useRef<AudioContext | null>(null);
  const ensureAudio = () => {
    if (!audioCtxRef.current) {
      try {
        const Ctx = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (Ctx) audioCtxRef.current = new Ctx();
      } catch {
        /* ignore */
      }
    }
    const c = audioCtxRef.current;
    if (c && c.state === "suspended") c.resume();
    return c;
  };
  const beep = (freq: number, dur = 0.12, type: OscillatorType = "sine", gain = 0.07) => {
    if (!audio) return;
    const ctx = ensureAudio();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    g.gain.setValueAtTime(0, ctx.currentTime);
    g.gain.linearRampToValueAtTime(gain, ctx.currentTime + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    osc.connect(g).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + dur + 0.02);
  };

  // Reset time when result changes
  useEffect(() => {
    setTime(0);
    setPlaying(false);
  }, [result]);

  // Animation loop
  useEffect(() => {
    if (!playing || !result) return;
    let raf = 0;
    let last = performance.now();
    const loop = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      setTime((cur) => {
        const next = cur + dt * speed;
        if (next >= result.totalRuntime) {
          setPlaying(false);
          return result.totalRuntime;
        }
        return next;
      });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [playing, result, speed]);

  const segs = result ? result.segments : [];

  // Compute current state at time
  const live = useMemo(() => {
    let depth = 0;
    let gasIdx = 0;
    let kind: string = "surface";
    let curSegIdx = -1;
    for (let i = 0; i < segs.length; i++) {
      const seg = segs[i]!;
      if (time >= seg.startTime && time <= seg.endTime) {
        const f = (time - seg.startTime) / Math.max(0.001, seg.duration);
        depth = seg.startDepth + (seg.endDepth - seg.startDepth) * f;
        gasIdx = seg.gasIdx;
        kind = seg.kind;
        curSegIdx = i;
        break;
      }
      if (time > seg.endTime) {
        depth = seg.endDepth;
        gasIdx = seg.gasIdx;
        kind = seg.kind;
        curSegIdx = i;
      }
    }
    return { depth, gasIdx, kind, curSegIdx };
  }, [time, segs]);

  const totalT = result ? result.totalRuntime : 0;
  const maxD = Math.max(result ? result.maxDepth : 0, 10);
  const gas = gases[live.gasIdx] ?? gases[0]!;
  const gasInfo = gasName(gas);
  const ppO2 = depthToAmbient(live.depth) * gas.fO2;

  let ceiling = 0;
  if (live.curSegIdx >= 0 && segs[live.curSegIdx]) {
    const gf = gfHigh / 100;
    ceiling = overallCeilingDepth(segs[live.curSegIdx]!.tissuesAfter, gf);
  }

  // Live tank pressure
  const cons = useMemo(() => {
    if (!result) return [];
    return consumptionForSegments(result.segments, {
      sac,
      tank: { volume: tankVolume, startPressure: tankStartPressure },
    });
  }, [result, sac, tankVolume, tankStartPressure]);

  let tankP = tankStartPressure;
  for (let i = 0; i < segs.length; i++) {
    const seg = segs[i]!;
    if (time >= seg.endTime) tankP = cons[i]!.tankPressureAfter;
    else if (time >= seg.startTime) {
      const f = (time - seg.startTime) / Math.max(0.001, seg.duration);
      tankP =
        cons[i]!.tankPressureBefore +
        (cons[i]!.tankPressureAfter - cons[i]!.tankPressureBefore) * f;
      break;
    }
  }

  const phases: Record<string, { fr: string; en: string; color: string }> = {
    descent: { fr: "Descente", en: "Descent", color: "var(--deep)" },
    bottom: { fr: "Fond", en: "Bottom", color: "var(--accent)" },
    ascent: { fr: "Remontée", en: "Ascent", color: "var(--safe)" },
    stop: { fr: "Palier", en: "Stop", color: "var(--caution)" },
    surface: { fr: "Surface", en: "Surface", color: "var(--ink-2)" },
  };
  const phase = phases[live.kind] ?? phases.surface!;
  const phaseLabel = lang === "fr" ? phase.fr : phase.en;

  // Audio cues
  const prevRef = useRef({ kind: "surface", hadCeiling: false, time: 0 });
  useEffect(() => {
    if (!result) return;
    const prev = prevRef.current;
    const ceilingNow = ceiling > 0.05;
    const movingForward = playing && time > prev.time;
    if (movingForward && ceilingNow && !prev.hadCeiling) {
      beep(880, 0.18, "sine", 0.08);
      setTimeout(() => beep(880, 0.18, "sine", 0.08), 220);
    }
    if (movingForward && live.kind === "stop" && prev.kind !== "stop") {
      beep(660, 0.14, "square", 0.05);
    }
    if (movingForward && prev.kind === "stop" && live.kind !== "stop") {
      beep(990, 0.14, "square", 0.05);
    }
    if (movingForward && prev.kind !== "surface" && live.depth < 0.5 && time >= totalT - 0.2) {
      beep(523, 0.12, "sine", 0.06);
      setTimeout(() => beep(659, 0.12, "sine", 0.06), 130);
      setTimeout(() => beep(784, 0.18, "sine", 0.06), 260);
    }
    prevRef.current = { kind: live.kind, hadCeiling: ceilingNow, time };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live.kind, ceiling, time, playing]);

  const emitBubbles = live.kind === "ascent" || live.kind === "stop" || live.kind === "bottom";

  const W = 360;
  const H = 150;
  const PAD = { l: 8, r: 8, t: 14, b: 8 };
  const pw = W - PAD.l - PAD.r;
  const ph = H - PAD.t - PAD.b;
  const xT = (tt: number) => PAD.l + (totalT > 0 ? (tt / totalT) * pw : 0);
  const yD = (d: number) => PAD.t + (d / maxD) * ph;

  const pts = [{ t: 0, d: 0 }];
  for (const s of segs) pts.push({ t: s.endTime, d: s.endDepth });
  const profilePath = pts
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xT(p.t).toFixed(1)} ${yD(p.d).toFixed(1)}`)
    .join(" ");

  const dx = xT(time);
  const dy = yD(live.depth);
  const headingDown = live.kind === "descent";
  const headingUp = live.kind === "ascent";

  const speedOpts = [1, 2, 6, 15, 60];

  const totalSec = Math.max(0, Math.floor(time * 60));
  const hh = String(Math.floor(totalSec / 3600)).padStart(2, "0");
  const mm = String(Math.floor((totalSec % 3600) / 60)).padStart(2, "0");
  const ss = String(totalSec % 60).padStart(2, "0");
  const diveClock = `${hh}:${mm}:${ss}`;

  if (!result) return null;

  return (
    <div style={{ padding: "0 14px 14px" }}>
      <div className="diver-readout">
        <div className="ro-cell phase" style={{ borderLeftColor: phase.color }}>
          <div className="ro-label">{lang === "fr" ? "Phase" : "Phase"}</div>
          <div className="ro-val md" style={{ color: phase.color }}>
            {phaseLabel.toUpperCase()}
          </div>
        </div>
        <div className="ro-cell">
          <div className="ro-label">{lang === "fr" ? "Prof." : "Depth"}</div>
          <div className="ro-val lg" style={{ color: "var(--accent)" }}>
            {live.depth.toFixed(1)}
            <span className="u">m</span>
          </div>
        </div>
        <div className="ro-cell">
          <div className="ro-label">{lang === "fr" ? "Plafond" : "Ceiling"}</div>
          <div
            className="ro-val lg"
            style={{ color: ceiling > 0 ? "var(--danger)" : "var(--safe)" }}
          >
            {ceiling > 0 ? ceiling.toFixed(1) : "—"}
            {ceiling > 0 && <span className="u">m</span>}
          </div>
        </div>
        <div className="ro-cell">
          <div className="ro-label">{lang === "fr" ? "Bouteille" : "Tank"}</div>
          <div
            className="ro-val lg"
            style={{
              color:
                tankP < 50 ? "var(--danger)" : tankP < 70 ? "var(--caution)" : "var(--ink)",
            }}
          >
            {tankP.toFixed(0)}
            <span className="u">bar</span>
          </div>
        </div>
      </div>

      <div className="diver-anim" style={{ height: H, position: "relative", overflow: "hidden" }}>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        >
          <defs>
            <linearGradient id="profileTrail" x1={0} y1={0} x2={1} y2={0}>
              <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.35} />
              <stop offset="100%" stopColor="var(--accent)" stopOpacity={0} />
            </linearGradient>
            <radialGradient id="sunRay" cx="50%" cy="0%" r="70%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.18)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0)" />
            </radialGradient>
            <radialGradient id="bubbleGrad" cx="30%" cy="30%">
              <stop offset="0%" stopColor="rgba(255,255,255,0.85)" />
              <stop offset="100%" stopColor="rgba(180,220,240,0.15)" />
            </radialGradient>
          </defs>

          <rect x={0} y={0} width={W} height={H} fill="url(#sunRay)" />

          <line
            x1={0}
            x2={W}
            y1={yD(0)}
            y2={yD(0)}
            stroke="var(--accent)"
            strokeWidth={0.75}
            opacity={0.5}
            strokeDasharray="2 2"
          />

          {/* Surface waves */}
          <path
            d={
              `M 0 ${(yD(0) - 1).toFixed(1)} ` +
              Array.from({ length: 24 }, (_, i) => {
                const x = (i / 23) * W;
                const wy = yD(0) - 1 + Math.sin(i * 0.6 + time * 0.4) * 1.2;
                return `L ${x.toFixed(1)} ${wy.toFixed(1)}`;
              }).join(" ")
            }
            stroke="var(--accent)"
            strokeWidth={0.6}
            fill="none"
            opacity={0.4}
          />

          {/* Future profile (faint) */}
          <path
            d={profilePath}
            stroke="var(--ink-3)"
            strokeWidth={1}
            fill="none"
            opacity={0.25}
            strokeDasharray="2 3"
          />

          {/* Safety stop band */}
          {maxD >= 3 && (
            <rect
              x={0}
              y={yD(3)}
              width={W}
              height={Math.max(0, yD(Math.min(6, maxD)) - yD(3))}
              fill="var(--safe)"
              opacity={0.08}
            />
          )}

          {/* Past trail */}
          {(() => {
            const trail = [{ t: 0, d: 0 }];
            for (const s of segs) {
              if (s.startTime <= time) {
                const endT = Math.min(s.endTime, time);
                const f = (endT - s.startTime) / Math.max(0.001, s.duration);
                trail.push({
                  t: endT,
                  d: s.startDepth + (s.endDepth - s.startDepth) * f,
                });
              }
            }
            return (
              <path
                d={trail
                  .map((p, i) => `${i === 0 ? "M" : "L"} ${xT(p.t).toFixed(1)} ${yD(p.d).toFixed(1)}`)
                  .join(" ")}
                stroke="var(--accent)"
                strokeWidth={1.75}
                fill="none"
                strokeLinejoin="round"
                filter="drop-shadow(0 0 3px rgba(93,208,232,0.6))"
              />
            );
          })()}

          {/* Deco stop ticks */}
          {result.decoStops.map((s, i) => {
            const seg = result.segments.find((sg) => sg.kind === "stop" && sg.startDepth === s.depth);
            if (!seg) return null;
            return (
              <rect
                key={`stop${i}`}
                x={xT(seg.startTime)}
                y={yD(s.depth) - 2}
                width={Math.max(2, xT(seg.endTime) - xT(seg.startTime))}
                height={4}
                rx={1}
                fill="var(--caution)"
                opacity={0.7}
              />
            );
          })}

          <line
            x1={dx}
            x2={dx}
            y1={0}
            y2={H}
            stroke="var(--accent)"
            strokeWidth={0.5}
            opacity={0.4}
            strokeDasharray="1 3"
          />

          {ceiling > 0 && (
            <g>
              <line
                x1={dx - 8}
                x2={dx + 8}
                y1={yD(ceiling)}
                y2={yD(ceiling)}
                stroke="var(--danger)"
                strokeWidth={1.5}
                strokeDasharray="2 2"
                opacity={0.85}
              />
              <text
                x={dx + 11}
                y={yD(ceiling) + 3}
                fill="var(--danger)"
                fontSize={8}
                fontFamily="var(--font-mono)"
                opacity={0.9}
              >
                {lang === "fr" ? "PLAFOND" : "CEILING"}
              </text>
            </g>
          )}

          {/* Bubbles */}
          {emitBubbles &&
            BUBBLES.map((b, i) => {
              const phase01 = ((time * b.speed + b.seed) / b.period) % 1;
              const startY = dy - 2;
              const endY = yD(0) + 2;
              const by = startY + (endY - startY) * phase01;
              const bx = dx + b.xOff + Math.sin(phase01 * Math.PI * b.wobble) * 3;
              if (by < yD(0) - 1) return null;
              const bsize = b.size * (1 + phase01 * 0.4);
              const opacity = 0.7 * (1 - phase01 * 0.5);
              return (
                <circle
                  key={`b${i}`}
                  cx={bx}
                  cy={by}
                  r={bsize}
                  fill="url(#bubbleGrad)"
                  opacity={opacity}
                />
              );
            })}

          {/* Diver */}
          <g transform={`translate(${dx} ${dy})`}>
            <circle cx={0} cy={0} r={14} fill="var(--accent)" opacity={0.12} />
            <circle cx={0} cy={0} r={9} fill="var(--accent)" opacity={0.18} />
            <g transform={`rotate(${headingDown ? 25 : headingUp ? -25 : 0})`}>
              <rect
                x={-2}
                y={-7}
                width={4}
                height={8}
                rx={1.5}
                fill="var(--accent-dim)"
                stroke="var(--accent)"
                strokeWidth={0.6}
              />
              <ellipse cx={0} cy={1} rx={4.5} ry={7} fill="var(--ink)" />
              <circle cx={0} cy={-6} r={3} fill="var(--ink)" stroke="var(--accent)" strokeWidth={0.5} />
              <rect x={-1.5} y={-7} width={3} height={1.5} rx={0.5} fill="var(--accent)" />
              <path d="M -3 7 L -5 11 L -1 9 Z" fill="var(--accent-dim)" />
              <path d="M 3 7 L 5 11 L 1 9 Z" fill="var(--accent-dim)" />
            </g>
          </g>

          {/* Time tick marks */}
          {(() => {
            const ticks = [];
            const step = totalT > 60 ? 15 : totalT > 30 ? 5 : 2;
            for (let tt = 0; tt <= totalT; tt += step) {
              ticks.push(
                <g key={`tk${tt}`}>
                  <line
                    x1={xT(tt)}
                    x2={xT(tt)}
                    y1={0}
                    y2={3}
                    stroke="var(--ink-3)"
                    strokeWidth={0.5}
                    opacity={0.5}
                  />
                  <text
                    x={xT(tt)}
                    y={11}
                    textAnchor="middle"
                    fontSize={8}
                    fontFamily="var(--font-mono)"
                    fill="var(--ink-3)"
                    opacity={0.7}
                  >
                    {tt}
                  </text>
                </g>,
              );
            }
            return ticks;
          })()}
        </svg>

        <div
          style={{
            position: "absolute",
            left: 8,
            bottom: 6,
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            color: "var(--ink-2)",
            background: "rgba(0,0,0,0.35)",
            padding: "2px 6px",
            borderRadius: 3,
            backdropFilter: "blur(4px)",
          }}
        >
          <span style={{ color: "var(--accent)", fontWeight: 600, letterSpacing: "0.04em" }}>
            {diveClock}
          </span>
          <span style={{ color: "var(--ink-3)", margin: "0 4px" }}>·</span>
          <span>
            t {time.toFixed(1)} / {totalT.toFixed(0)} min
          </span>
        </div>

        <div
          style={{
            position: "absolute",
            right: 8,
            bottom: 6,
            display: "flex",
            gap: 6,
            alignItems: "center",
            background: "rgba(0,0,0,0.35)",
            padding: "2px 6px",
            borderRadius: 3,
            backdropFilter: "blur(4px)",
          }}
        >
          <span className={`gas-tag ${gasInfo.cls}`} style={{ fontSize: 9, padding: "1px 4px" }}>
            {gasInfo.tag}
          </span>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color:
                ppO2 > 1.6 ? "var(--danger)" : ppO2 > 1.4 ? "var(--warn)" : "var(--ink-2)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            ppO₂ {ppO2.toFixed(2)}
          </span>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10 }}>
        <button
          className="btn btn-ghost"
          style={{ padding: "5px 10px", minWidth: 32, justifyContent: "center" }}
          onClick={() => {
            if (time >= totalT) setTime(0);
            setPlaying((p) => !p);
          }}
          title={playing ? (lang === "fr" ? "Pause" : "Pause") : lang === "fr" ? "Lire" : "Play"}
        >
          {playing ? <Pause size={13} /> : <Play size={13} />}
        </button>
        <button
          className="btn btn-ghost"
          style={{ padding: "5px 10px", minWidth: 32, justifyContent: "center" }}
          onClick={() => {
            setPlaying(false);
            setTime(0);
          }}
          title={lang === "fr" ? "Réinitialiser" : "Reset"}
        >
          <Reset size={13} />
        </button>
        <input
          type="range"
          min={0}
          max={totalT}
          step={0.1}
          value={time}
          onChange={(e) => {
            setPlaying(false);
            setTime(Number(e.target.value));
          }}
          className="gf-slider"
          style={{ flex: 1 }}
        />
        <div className="seg" style={{ fontSize: 9 }}>
          {speedOpts.map((s) => (
            <button
              key={s}
              className={speed === s ? "on" : ""}
              onClick={() => setSpeed(s)}
              title={`${s}× simulated minutes per real second`}
            >
              {s}×
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
